import { Box, Optional, useMediaQueries } from '@honda-canada/design-system-react';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CommonModal from '../CommonModal/CommonModal';
import Error from '../PriceComponent/Error';
import Loader from '../../utils/components/Loader';
import ShareYourBuild from '../ShareYourBuild';
import SummaryCardHeader from './SummaryCardHeader';
import SummaryCardInfo from './SummaryCardInfo';
import SummaryCardPayment from './SummaryCardPayment';
import gtmForGenericCtaLinkClick from '../../core/gtm/gtmForGenericCtaLinkClick';
import isSSR from '../../utils/isSSR';
import { removeAllTagsFromMarkdown } from '../../utils/markdown';
import { selectBodyTypesForGtm } from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import themeStyles from './SummaryCard.styles';
import { useAppSelector } from '../../core/utils';
import useSummaryCard from './hooks/useSummaryCard';
const SummaryCardComponent = themeStyles.apply(Box, 'SummaryCardComponent');
const SummaryCardWrapper = themeStyles.apply(Box, 'SummaryCardWrapper');
const SummaryCard = ({ fields, rendering: { componentName } }) => {
    var _a, _b;
    const stickToComponentDesktop = (_a = fields === null || fields === void 0 ? void 0 : fields.stickToComponentDesktop) === null || _a === void 0 ? void 0 : _a.name;
    const stickToComponentMobile = (_b = fields === null || fields === void 0 ? void 0 : fields.stickToComponentMobile) === null || _b === void 0 ? void 0 : _b.name;
    const [isSticky, setIsSticky] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [height, setHeight] = useState(0);
    const { t } = useTranslation();
    const { isDesktop } = useMediaQueries();
    const { paymentData, showLoader, showError, nameBadge, vehicle, modelImage, currentPaymentMethodCash, includeFees, overriddenIsSellingProvince, paymentMethod, paymentFrequency, bigExteriorImage, } = useSummaryCard();
    const { inventoryStatus: availabilityFlag } = useAppSelector(selectCurrentVehicle);
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const gtmSharedInfo = useMemo(() => {
        var _a, _b;
        return ({
            componentType: componentName,
            model: (_a = vehicle === null || vehicle === void 0 ? void 0 : vehicle.modelName) !== null && _a !== void 0 ? _a : '',
            trim: (_b = removeAllTagsFromMarkdown(vehicle === null || vehicle === void 0 ? void 0 : vehicle.trimName)) !== null && _b !== void 0 ? _b : '',
            gtmBodyStyle,
            availabilityFlag,
        });
    }, [vehicle, gtmBodyStyle, availabilityFlag, componentName]);
    useLayoutEffect(() => {
        if (isSSR())
            return;
        const stickToComponentComponentName = isDesktop ? stickToComponentDesktop : stickToComponentMobile;
        if (!stickToComponentComponentName)
            return;
        const element = document.querySelector(`#${stickToComponentComponentName}`);
        setHeight(prevHeight => { var _a, _b; return (_b = (_a = element === null || element === void 0 ? void 0 : element.getBoundingClientRect()) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : prevHeight; });
    }, [isDesktop, isSticky]);
    useEffect(() => {
        if (!isDesktop)
            return;
        const bapProgressBar = document.querySelector(`#${stickToComponentDesktop}`);
        if (!bapProgressBar)
            return;
        let isBusy = false;
        const callback = () => {
            if (isBusy)
                return;
            requestAnimationFrame(() => {
                isBusy = true;
                setIsSticky(bapProgressBar.getBoundingClientRect().top <= 0);
                isBusy = false;
            });
        };
        window.addEventListener('scroll', callback);
        return () => {
            window.removeEventListener('scroll', callback);
        };
    }, [isDesktop]);
    return (React.createElement(SummaryCardComponent, { "data-component-name": componentName, componentType: componentName, stickyOffsetTop: height, "data-testid": "cy-summary_card" },
        React.createElement(SummaryCardWrapper, { isExpanded: isSticky },
            React.createElement(Optional, { when: showLoader },
                React.createElement(Loader, { testId: "cy-summary-card-loading-state" })),
            React.createElement(Optional, { when: showError },
                React.createElement(Error, { message: t('Pages.Bap.apiLoadingError'), verticalAlignment: "center", errorIconName: "exclamationAlert" })),
            React.createElement(Optional, { when: !showLoader && !showError },
                React.createElement(SummaryCardHeader, { nameBadge: nameBadge, vehicle: vehicle, paymentData: paymentData, includeFees: includeFees, overriddenIsSellingProvince: overriddenIsSellingProvince }),
                React.createElement(Optional, { when: !currentPaymentMethodCash },
                    React.createElement(SummaryCardPayment, { paymentMethod: paymentMethod, paymentData: paymentData, paymentFrequency: paymentFrequency })),
                React.createElement(SummaryCardInfo, { modelImage: modelImage, isExpanded: isSticky, setShowModal: setShowModal, gtmFields: gtmSharedInfo }))),
        React.createElement(CommonModal, { handleCloseModal: () => {
                setShowModal(false);
                gtmForGenericCtaLinkClick({
                    ...gtmSharedInfo,
                    eventName: 'cta_click',
                    ctaType: 'click',
                    customizeSection: 'share build',
                    linkText: 'x',
                    linkUrl: '',
                });
            }, handleSubmit: () => null, showModal: showModal, title: t('Pages.Bap.shareYourBuildLabel') },
            React.createElement(ShareYourBuild, { bigExteriorImage: bigExteriorImage, nameBadge: nameBadge, vehicle: vehicle, paymentData: paymentData, modelImage: modelImage, gtmFields: gtmSharedInfo }))));
};
export default SummaryCard;
